import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface UsageRow { action: string; units: number; costCents: number }
type Plan = 'starter' | 'pro' | 'business'

const PLAN_LABEL: Record<Plan, { name: string; price: string; tagline: string }> = {
  starter:  { name: 'Starter',  price: '$49/mo',  tagline: '1 site · 5 scans · report-only' },
  pro:      { name: 'Pro',      price: '$199/mo', tagline: '5 sites · PR mode' },
  business: { name: 'Business', price: '$799/mo', tagline: '25 sites · all apply modes' },
}

export function BillingPage() {
  const t = useT()

  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => apiClient.get<{ plan: string; data: UsageRow[] }>('/billing/usage'),
  })
  const aiSpend = useQuery({
    queryKey: ['ai-spend'],
    queryFn: () => apiClient.get<{ costCents: number }>('/billing/ai-spend'),
  })

  const checkout = useMutation({
    mutationFn: (plan: Plan) =>
      apiClient.post<{ url: string }>('/billing/checkout', { plan, successUrl: window.location.origin + '/billing?welcome=1' }),
    onSuccess: ({ url }) => { window.location.href = url },
  })

  const portal = useMutation({
    mutationFn: () => apiClient.post<{ url: string }>('/billing/portal'),
    onSuccess: ({ url }) => { window.location.href = url },
  })

  if (usage.isLoading) return <p>{t('common.loading')}</p>
  if (usage.isError || !usage.data) return <p>{t('common.error')}</p>

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">{t('billing.title')}</h1>
      <p className="text-sm opacity-70 mb-4">
        {t('billing.plan')}: <strong>{usage.data.plan}</strong>
      </p>

      {usage.data.plan !== 'enterprise' && (
        <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {(['starter', 'pro', 'business'] as Plan[]).map((p) => (
            <div
              key={p}
              className="p-4 rounded border"
              style={{
                borderColor: 'var(--color-border)',
                outline: usage.data.plan === p ? '2px solid var(--color-accent)' : undefined,
              }}
            >
              <div className="text-xs font-medium uppercase tracking-wide opacity-70">{PLAN_LABEL[p].name}</div>
              <div className="text-2xl font-bold mt-1">{PLAN_LABEL[p].price}</div>
              <div className="text-xs opacity-70 mt-1">{PLAN_LABEL[p].tagline}</div>
              <button
                type="button"
                disabled={checkout.isPending || usage.data.plan === p}
                onClick={() => checkout.mutate(p)}
                className="mt-3 px-3 py-1.5 text-sm rounded font-medium w-full"
                style={{
                  background: usage.data.plan === p ? 'transparent' : 'var(--color-accent)',
                  color: usage.data.plan === p ? 'inherit' : 'white',
                  border: usage.data.plan === p ? '1px solid var(--color-border)' : undefined,
                }}
              >
                {usage.data.plan === p ? 'Current' : 'Switch'}
              </button>
            </div>
          ))}
        </section>
      )}

      <div className="mb-6 flex items-center gap-3 text-sm">
        <button
          type="button"
          onClick={() => portal.mutate()}
          disabled={portal.isPending}
          className="px-3 py-1.5 rounded border font-medium"
          style={{ borderColor: 'var(--color-border)' }}
        >
          Manage subscription
        </button>
        {aiSpend.data && (
          <span className="opacity-70">
            AI spend this cycle: ${(aiSpend.data.costCents / 100).toFixed(2)}
          </span>
        )}
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left opacity-70">
            <th className="py-2 pr-4">{t('billing.usage.action')}</th>
            <th className="py-2 pr-4">{t('billing.usage.units')}</th>
            <th className="py-2 pr-4">{t('billing.usage.cost')}</th>
          </tr>
        </thead>
        <tbody>
          {usage.data.data.map((r) => (
            <tr key={r.action} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
              <td className="py-2 pr-4">{r.action}</td>
              <td className="py-2 pr-4">{r.units.toLocaleString()}</td>
              <td className="py-2 pr-4">{r.costCents.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
